import { Upload } from 'lucide-react'
import { useState } from 'react'

import { type Medio, useSubirMedio, useUsoDeMedios } from '../api/medios'
import { Aviso, Boton, claseDeEntrada, clases } from '../componentes/ui'

/**
 * Subir **una imagen** por la subida de siempre (`POST /api/medios`), con su texto alternativo:
 * lo que hace el panel cuando la biblioteca v2 está apagada en la instalación (`MEDIOS_V2_ACTIVO`,
 * plan §21). El núcleo la analiza igual —su tipo de verdad y ClamAV— antes de guardarla; sin
 * lotes, sin documentos, sin SVG y sin privados. Lo que pasa del máximo del plan se reduce en
 * el navegador antes de subirlo, como siempre.
 */
export const SubidaDeUnaImagen = ({ sitio, espacio, alSubir, texto = 'Subir y elegir' }: { alSubir: (m: Medio) => void; espacio: number; sitio: number; texto?: string }) => {
  const subir = useSubirMedio(sitio, espacio)
  const { data: uso } = useUsoDeMedios(espacio)
  const [fichero, setFichero] = useState<File | null>(null)
  const [alt, setAlt] = useState('')
  const [arrastrando, setArrastrando] = useState(false)
  const elegir = (f: File | null) => {
    setFichero(f)
    if (f && !alt) setAlt(f.name.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' '))
  }
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault()
        if (!fichero) return
        subir.mutate({ fichero, alt: alt.trim(), maximoBytes: uso?.maximoPorFicheroBytes }, { onSuccess: (m) => alSubir(m) })
      }}
    >
      <label
        className={clases(
          'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-caja border-2 border-dashed px-4 py-8 text-center transition-colors',
          arrastrando ? 'border-violeta bg-violeta-suave' : 'border-linea-fuerte bg-papel-2 hover:border-violeta',
        )}
        onDragLeave={() => setArrastrando(false)}
        onDragOver={(e) => {
          e.preventDefault()
          setArrastrando(true)
        }}
        onDrop={(e) => {
          e.preventDefault()
          setArrastrando(false)
          elegir(e.dataTransfer.files?.[0] ?? null)
        }}
      >
        <Upload className="size-6 text-tenue" />
        <span className="break-all text-[13px] font-medium">{fichero ? fichero.name : 'Arrastra una imagen o pulsa para elegirla'}</span>
        <span className="text-xs text-tenue">JPG, PNG, WebP, AVIF o GIF</span>
        <input accept="image/jpeg,image/png,image/webp,image/avif,image/gif" className="sr-only" onChange={(e) => elegir(e.target.files?.[0] ?? null)} type="file" />
      </label>
      <label className="flex flex-col gap-1.5">
        <span className="text-[13px] font-medium">Texto alternativo</span>
        <input className={claseDeEntrada()} onChange={(e) => setAlt(e.target.value)} placeholder="Qué se ve en la imagen" required value={alt} />
        <span className="text-xs text-tenue">Lo leen quienes no ven la imagen y los buscadores. Es obligatorio.</span>
      </label>
      {subir.error && <Aviso tono="error">{subir.error.message}</Aviso>}
      <div className="flex flex-wrap items-center gap-3">
        <Boton cargando={subir.isPending} disabled={!fichero || !alt.trim()} icono={<Upload />} tipo="primario" type="submit">
          {texto}
        </Boton>
        {subir.reduciendo && (
          <span className="text-[13px] text-tenue" role="status">
            Reduciendo «{subir.reduciendo}»…
          </span>
        )}
      </div>
    </form>
  )
}
